import { Link } from 'react-router-dom';
import {
	ArrowUpRight,
	BarChart3,
	Check,
	Inbox,
	Star,
	Users,
	Workflow,
	Zap,
	ShieldCheck,
	Database,
} from 'lucide-react';

import FaqAccordion, { type FaqItem } from './components/FaqAccordion';
import LogoStrip from './components/LogoStrip';
import MockFrame from './components/MockFrame';
import './landing.css';

// Marketing landing for Enflux. Same layout system as the Figma: dark navy with
// edge-anchored blooms, one bento panel, marquee logo strip. Copy is final; the
// product shots are not. Pass `src` to the <MockFrame> calls once they land.
const CONTAINER = 'mx-auto w-full max-w-[1240px] px-6';

const HERO_SUBHEAD =
	'Enflux combines performance history, live trends, and your Brand Kit into one centralized workspace so your team stops guessing and starts shipping.';

// Social-proof stack. Placeholder portraits served from public/avatars.
// Swap for real customer photos when they exist.
const AVATARS = ['/avatars/user-1.jpg', '/avatars/user-2.jpg', '/avatars/user-3.jpg', '/avatars/user-4.jpg'];

const FEATURES = [
	{
		icon: Inbox,
		title: 'For You Recommendations',
		description:
			'Stop staring at blank schedules. Enflux mines engagement history, trend signals, and time-slot data into a ranked feed that tells you what to post next.',
	},
	{
		icon: Zap,
		title: 'Weekly Trend Velocity',
		description:
			'Spot opportunities before they disappear. Our engine tracks topic momentum across platforms week over week, feeding fresh signals into your prompts.',
	},
	{
		icon: Users,
		title: 'Creator & Competitor Research',
		description:
			'Search handles on TikTok, Instagram, YouTube, or X to analyze profile metrics, review top posts, and pull transcripts into your planning board.',
	},
	{
		icon: BarChart3,
		title: 'On-Brand AI Generation',
		description:
			'Generate content that sounds like your client. Every prompt automatically applies your saved Brand Kit including voice, tone, and audience context.',
	},
];

const STATS = [
	{ value: '7', label: 'Channels Supported' },
	{ value: '100%', label: 'Server-Enforced Approvals' },
	{ value: '1', label: 'Centralized Social Inbox' },
	{ value: '24/7', label: 'Automated Token Refreshes' },
];

const PLANS = [
	{
		name: 'Entry',
		description: 'For one brand taking control of its own channels.',
		price: '$20',
		period: '/ month',
		featured: false,
		features: [
			'1 workspace, one account per network',
			'All 7 platform integrations',
			'AI Studio for copy and images',
			'Unified inbox, calendar and queues',
			'Shareable monthly reports',
		],
	},
	{
		name: 'Freelancer',
		description: 'For a small agency looking after a handful of brands.',
		price: '$60',
		period: '/ month',
		featured: true,
		features: [
			'Up to 5 client workspaces',
			'Everything in Entry',
			'Client approval workflow',
			'Creator research and weekly trends',
			'Evergreen recycling and Morning Brief',
		],
	},
	{
		name: 'Agency',
		description: 'For agencies scaling across a full client roster.',
		price: '$110',
		period: '/ month',
		featured: false,
		features: [
			'Up to 20 client workspaces',
			'Everything in Freelancer',
			'White-label reports in client branding',
			'Canva integration',
			'Add extra brands whenever you win them',
		],
	},
];

// Bullet points for the two split sections. Label renders bold, detail after it.
const RECOMMENDATION_POINTS = [
	{
		label: 'Phoenix Engine',
		detail: 'Analyzes past performance to surface proven concepts.',
	},
	{
		label: 'Trend Intelligence',
		detail: 'Connects real-time topic acceleration directly into your post drafting process.',
	},
	{
		label: 'Continuous Learning',
		detail: 'Adapts to your team’s approvals to make future recommendations smarter.',
	},
];

const WORKSPACE_POINTS = [
	{
		label: 'Centralized Inbox',
		detail: 'Manage comments, DMs, and mentions in one clean stream.',
	},
	{
		label: 'Automated Reports',
		detail: 'Build white-label PDF reports with AI summaries.',
	},
	{
		label: 'Strict Controls',
		detail: 'Set role-based permissions to safeguard publishing queues.',
	},
];

const FAQS: FaqItem[] = [
	{
		question: 'Which social platforms does Enflux support?',
		answer:
			'Enflux supports X (Twitter), Instagram, Facebook, LinkedIn, TikTok, YouTube, and Google Business Profile with customized controls for each channel.',
	},
	{
		question: 'How is the For You feed different from a standard social media scheduler?',
		answer:
			'A scheduler waits for you to decide what to post. The For You feed arrives already ranked: Enflux reads that client’s engagement history, the week’s trend signals and the gaps in their schedule, then tells you what to post next and when.',
	},
	{
		question: 'How does the AI keep content on-brand for each client?',
		answer:
			'Every client workspace holds its own Brand Kit covering voice, tone, target audience and services. That kit is attached to every prompt automatically, so drafts sound like the brand rather than like a chatbot.',
	},
	{
		question: 'Can we force mandatory approvals before anything goes live?',
		answer:
			'Yes, and it is enforced on the server rather than by convention. Route any post to a client or a manager for sign-off and it cannot publish until someone approves it, whatever anyone clicks.',
	},
	{
		question: 'How do we get access to Enflux?',
		answer:
			'Request a demo and we will walk through your client roster with you, set up your first workspace, and connect your accounts on the call.',
	},
];

const FOOTER_COLUMNS = [
	{
		title: 'Product',
		links: [
			{ label: 'Publishing', href: '#features' },
			{ label: 'Calendar & queues', href: '#features' },
			{ label: 'Unified inbox', href: '#features' },
		],
	},
	{
		title: 'Intelligence',
		links: [
			{ label: 'AI Studio', href: '#features' },
			{ label: 'Creator research', href: '#features' },
			{ label: 'Analytics & reports', href: '#features' },
		],
	},
	{
		title: 'Company',
		links: [
			{ label: 'Pricing', href: '#pricing' },
			{ label: 'FAQ', href: '#faq' },
			{ label: 'Contact', href: 'mailto:contact@enflux.cc' },
		],
	},
];

export default function LandingPage() {
	return (
		<div className="lp min-h-screen">
			<main>
				{/* ── Hero (header rides on this same surface) ─────────────────── */}
				<section className="lp-hero-bg relative overflow-hidden">
					<div className="lp-grid-lines pointer-events-none absolute inset-0" aria-hidden />

					<header
						className={`${CONTAINER} relative z-20 flex h-[76px] items-center justify-between`}
					>
						<Link to="/" className="flex items-center">
							<img src="/enflux-logo.png" alt="Enflux" className="h-[26px] w-auto" />
						</Link>

						<Link to="/sign-in" className="lp-btn lp-btn-primary lp-btn-sm">
							Sign in
							<ArrowUpRight className="h-3.5 w-3.5" />
						</Link>
					</header>

					<div className={`${CONTAINER} relative pb-16 pt-14 text-center`}>
						<span className="lp-pill">Social Media &amp; Trend Operations</span>

						<h1 className="lp-h1 mx-auto mt-7 max-w-[24ch]">
							Never open an empty
							<br className="hidden sm:block" /> content calendar again.
						</h1>

						<p className="mx-auto mt-6 max-w-[62ch] text-[15px] leading-relaxed text-white/[0.55]">
							{HERO_SUBHEAD}
						</p>

						{/* Social proof */}
						<div className="mt-8 flex flex-col items-center gap-2">
							<div className="flex items-center gap-4">
								<div className="flex items-center" aria-hidden>
									{AVATARS.map((src, i) => (
										<img
											key={src}
											src={src}
											alt=""
											loading="lazy"
											className="relative -ml-2 h-9 w-9 rounded-full border-2 border-[#0a1a30] object-cover first:ml-0"
											style={{ zIndex: AVATARS.length - i }}
										/>
									))}
								</div>
								<div className="flex items-center gap-1" aria-label="Rated 5 out of 5">
									{[0, 1, 2, 3, 4].map((i) => (
										<Star key={i} className="h-5 w-5 fill-[#3ab7f2] text-[#3ab7f2]" />
									))}
								</div>
							</div>
							<p className="text-xs text-white/[0.45]">Over 500 satisfied users</p>
						</div>

						{/* CTAs */}
						<div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
							<Link to="/sign-up" className="lp-btn lp-btn-primary">
								Request a Demo
								<ArrowUpRight className="h-4 w-4" />
							</Link>
							<a href="#features" className="lp-btn lp-btn-ghost">
								See How It Works
								<ArrowUpRight className="h-4 w-4" />
							</a>
						</div>
					</div>

					{/* Product panel */}
					<div className="lp-panel-glow mx-auto w-full max-w-[1180px] px-6 pb-20">
						<MockFrame
							label="The Enflux dashboard, showing the Morning Brief and ranked content suggestions for a client workspace"
							src="/shots/dashboard.png"
							aspect="aspect-[2/1]"
							className="w-full"
						/>
					</div>
				</section>

				{/* ── Logo strip ───────────────────────────────────────────────── */}
				<section className="pb-24 pt-16">
					<div className="mb-9 flex items-center justify-center gap-5 px-6">
						<span
							className="h-px w-[220px] max-w-[20vw] bg-gradient-to-r from-transparent to-white/25"
							aria-hidden
						/>
						<p className="text-center text-[13px] font-semibold tracking-tight text-white">
							Connected Social Platforms &amp; Integrations
						</p>
						<span
							className="h-px w-[220px] max-w-[20vw] bg-gradient-to-l from-transparent to-white/25"
							aria-hidden
						/>
					</div>

					<div className={CONTAINER}>
						<LogoStrip />
					</div>
				</section>

				{/* ── Features ─────────────────────────────────────────────────── */}
				<section id="features" className="relative py-24">
					<div className="lp-deco-left" aria-hidden />

					<div className={`${CONTAINER} relative`}>
						<div className="grid items-end gap-8 lg:grid-cols-[1.1fr_1fr]">
							<h2 className="lp-h2 max-w-[22ch]">
								Everything your team needs to run social at scale.
							</h2>
							<p className="max-w-[52ch] text-sm leading-relaxed text-white/50 lg:justify-self-end">
								A centralized suite of tools designed to help social media agencies automate
								content discovery, enforce strict approval workflows, and scale client results
								without multiplying operational overhead.
							</p>
						</div>

						<div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
							{FEATURES.map((feature) => {
								const Icon = feature.icon;
								return (
									<article
										key={feature.title}
										className="lp-card lp-card-hover lp-sheen flex flex-col p-6"
									>
										<span className="lp-icon-tile mb-5">
											<Icon className="h-[18px] w-[18px]" />
										</span>
										<h3 className="lp-h3 mb-2.5">{feature.title}</h3>
										<p className="text-[13px] leading-relaxed text-white/[0.45]">
											{feature.description}
										</p>
									</article>
								);
							})}
						</div>
					</div>
				</section>

				{/* ── Bento + stats ────────────────────────────────────────────── */}
				<section className="relative py-24">
					<div className="lp-deco-right-bottom" aria-hidden />

					<div className={`${CONTAINER} relative`}>
						<div className="mx-auto max-w-[46ch] text-center">
							<h2 className="lp-h2">
								Built for agency speed, collaboration, and total control.
							</h2>
						</div>

						<div className="lp-bento mt-14">
							<div className="grid gap-3 lg:grid-cols-3 lg:grid-rows-[auto_auto]">
								{/* Left column: tall panel spanning both rows */}
								<MockFrame
									label="The Enflux content calendar with scheduled posts across a client's connected accounts"
									src="/shots/calendar.png"
									fit="hug"
									className="lg:row-span-2 lg:self-start"
								/>

								<article className="lp-card lp-sheen p-6">
									<span className="lp-icon-tile mb-5">
										<Zap className="h-[18px] w-[18px]" />
									</span>
									<h3 className="lp-h3 mb-2.5">Multi-Channel Publishing</h3>
									<p className="text-[13px] leading-relaxed text-white/[0.45]">
										Draft, schedule, and publish content across 7 major social platforms from a
										single centralized interface.
									</p>
								</article>

								<article className="lp-card lp-sheen p-6">
									<span className="lp-icon-tile mb-5">
										<ShieldCheck className="h-[18px] w-[18px]" />
									</span>
									<h3 className="lp-h3 mb-2.5">Brand Kit Engine</h3>
									<p className="text-[13px] leading-relaxed text-white/[0.45]">
										Store client voice, tone, target audience, and services to keep every AI draft
										automatically aligned.
									</p>
								</article>

								<article className="lp-card lp-sheen flex flex-col p-6">
									<span className="lp-icon-tile mb-5">
										<Database className="h-[18px] w-[18px]" />
									</span>
									<h3 className="lp-h3 mb-2.5">Dedicated Agency Governance</h3>
									<p className="text-[13px] leading-relaxed text-white/[0.45]">
										Enforce mandatory client or manager sign-offs so that no unapproved drafts
										ever hit publishing queues.
									</p>
									<Link to="/sign-up" className="lp-btn lp-btn-primary lp-btn-sm mt-6 self-start">
										Explore Workflows
										<ArrowUpRight className="h-3.5 w-3.5" />
									</Link>
								</article>

								<MockFrame
									label="AI Studio generating a branded image from a written prompt"
									src="/shots/ai-studio.png"
									aspect="aspect-[16/9]"
								/>
							</div>
						</div>

						<div className="mt-20 grid grid-cols-2 gap-8 text-center lg:grid-cols-4">
							{STATS.map((stat) => (
								<div key={stat.label}>
									<div className="lp-num text-[2.4rem]">{stat.value}</div>
									<p className="mt-2.5 text-[13px] text-white/[0.45]">{stat.label}</p>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* ── Split section: copy left, panel right ────────────────────── */}
				<section className="relative py-24">
					<div className={`${CONTAINER} relative`}>
						<div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
							<div>
								<span className="lp-pill">Recommendation Engine</span>
								<h2 className="lp-h2 mt-6 max-w-[22ch]">
									Smart content discovery built around your strategy.
								</h2>
								<p className="mt-6 max-w-[52ch] text-sm leading-relaxed text-white/50">
									Stop wasting hours brainstorming from scratch. Enflux brings together key
									performance signals, timing indicators, and audience feedback to feed your daily
									strategy automatically.
								</p>
								<ul className="mt-7 flex flex-col gap-3.5">
									{RECOMMENDATION_POINTS.map((point) => (
										<li key={point.label} className="flex items-start gap-2.5">
											<span className="mt-1 grid h-4 w-4 flex-none place-items-center rounded-full bg-[#00c7f9]/[0.15] text-[#7fe4ff]">
												<Check className="h-2.5 w-2.5" />
											</span>
											<p className="max-w-[48ch] text-[13px] leading-relaxed text-white/50">
												<span className="font-medium text-white/80">{point.label}:</span>{' '}
												{point.detail}
											</p>
										</li>
									))}
								</ul>
								<Link to="/sign-up" className="lp-btn lp-btn-primary mt-8">
									See Recommendation Flow
									<ArrowUpRight className="h-4 w-4" />
								</Link>
							</div>

							<MockFrame
								label="The Enflux post composer, with per-platform tabs and the client's connected accounts"
								src="/shots/composer.png"
								aspect="aspect-[16/9]"
							/>
						</div>
					</div>
				</section>

				{/* ── Split section mirrored: panel left, copy right ───────────── */}
				<section className="relative py-24">
					<div className={`${CONTAINER} relative`}>
						<div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
							<MockFrame
								label="A monthly client report showing engagement, follower growth and best performing posts"
								src="/shots/monthly-report.png"
								aspect="aspect-[16/9]"
							/>

							<div>
								<span className="lp-pill">Unified Workspace</span>
								<h2 className="lp-h2 mt-6 max-w-[22ch]">
									From idea to approved post without changing tools
								</h2>
								<p className="mt-6 max-w-[52ch] text-sm leading-relaxed text-white/50">
									Keep your team in sync from draft to report. Customize content per platform,
									organize media assets, route posts through required approval steps, and manage
									feedback seamlessly.
								</p>
								<ul className="mt-7 flex flex-col gap-3.5">
									{WORKSPACE_POINTS.map((point) => (
										<li key={point.label} className="flex items-start gap-2.5">
											<span className="mt-1 grid h-4 w-4 flex-none place-items-center rounded-full bg-[#00c7f9]/[0.15] text-[#7fe4ff]">
												<Check className="h-2.5 w-2.5" />
											</span>
											<p className="max-w-[48ch] text-[13px] leading-relaxed text-white/50">
												<span className="font-medium text-white/80">{point.label}:</span>{' '}
												{point.detail}
											</p>
										</li>
									))}
								</ul>
								<Link to="/sign-up" className="lp-btn lp-btn-primary mt-8">
									View Agency Tools
									<ArrowUpRight className="h-4 w-4" />
								</Link>
							</div>
						</div>
					</div>
				</section>

				{/* ── Pricing ──────────────────────────────────────────────────── */}
				<section id="pricing" className={`${CONTAINER} py-24`}>
					<span className="lp-pill">Pricing</span>
					<div className="mt-6 grid items-end gap-8 lg:grid-cols-[1.1fr_1fr]">
						<h2 className="lp-h2 max-w-[20ch]">Simple plans tailored to your agency scale.</h2>
						<p className="max-w-[48ch] text-sm leading-relaxed text-white/50 lg:justify-self-end">
							An intuitive pricing model designed to scale seamlessly as your client portfolio
							grows, paired with answers to everything you need to know about getting started.
						</p>
					</div>

					<div className="mt-14 grid gap-6 lg:grid-cols-3">
						{PLANS.map((plan) => (
							<article
								key={plan.name}
								className={`lp-card lp-sheen relative flex flex-col p-7 ${
									plan.featured ? 'lp-card-featured lg:-mt-4 lg:mb-4' : ''
								}`}
							>
								{plan.featured && (
									<span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#00c7f9] px-3 py-1 text-[11px] font-semibold text-[#04121f]">
										Best Deal
									</span>
								)}

								<h3 className="lp-h3 mb-3">{plan.name}</h3>
								<p className="min-h-[3.5rem] text-[13px] leading-relaxed text-white/[0.45]">
									{plan.description}
								</p>

								<div className="mt-6 flex items-baseline gap-1.5">
									<span className="lp-num text-[2.6rem] text-white">{plan.price}</span>
									<span className="text-[13px] text-white/40">{plan.period}</span>
								</div>

								<Link
									to="/sign-up"
									className={`lp-btn mt-7 w-full ${
										plan.featured ? 'lp-btn-primary' : 'lp-btn-ghost'
									}`}
								>
									Choose {plan.name}
									<ArrowUpRight className="h-4 w-4" />
								</Link>

								<p className="mb-4 mt-7 text-[13px] font-medium text-white/70">
									Including features:
								</p>
								<ul className="flex flex-col gap-3">
									{plan.features.map((feature) => (
										<li
											key={feature}
											className="flex items-start gap-2.5 text-[13px] text-white/50"
										>
											<span className="mt-0.5 grid h-4 w-4 flex-none place-items-center rounded-full bg-[#00c7f9]/[0.15] text-[#7fe4ff]">
												<Check className="h-2.5 w-2.5" />
											</span>
											{feature}
										</li>
									))}
								</ul>
							</article>
						))}
					</div>
				</section>

				{/* ── FAQ ──────────────────────────────────────────────────────── */}
				<section id="faq" className={`${CONTAINER} pb-24`}>
					<div className="lp-card lp-sheen grid gap-10 p-8 lg:grid-cols-[0.85fr_1.15fr] lg:p-10">
						<div className="flex flex-col gap-6">
							<div>
								<h2 className="lp-h2 max-w-[16ch]">Clear answers for agency leaders.</h2>
							</div>

							<div className="lp-card mt-auto p-6">
								<h3 className="lp-h3 mb-2.5">Have more questions?</h3>
								<p className="max-w-[38ch] text-[13px] leading-relaxed text-white/[0.45]">
									Need a custom walkthrough for your agency? Schedule a private demo with our
									product team.
								</p>
								<a href="mailto:contact@enflux.cc" className="lp-btn lp-btn-primary lp-btn-sm mt-6">
									Schedule Demo
									<ArrowUpRight className="h-3.5 w-3.5" />
								</a>
							</div>
						</div>

						<FaqAccordion items={FAQS} />
					</div>
				</section>
			</main>

			{/* ── Footer ───────────────────────────────────────────────────── */}
			<footer className="border-t border-white/[0.08] bg-[#050f1d]">
				<div className={`${CONTAINER} grid gap-10 py-14 lg:grid-cols-[1.6fr_1fr_1fr_1fr]`}>
					<div>
						<img src="/enflux-logo.png" alt="Enflux" className="h-[26px] w-auto" />
						<p className="mt-6 text-[13px] text-white/40">Support:</p>
						<a
							href="mailto:contact@enflux.cc"
							className="text-[13px] text-white/70 transition-colors hover:text-white"
						>
							contact@enflux.cc
						</a>
					</div>

					{FOOTER_COLUMNS.map((column, i) => (
						<div key={`${column.title}-${i}`}>
							<h3 className="lp-h4 mb-4">{column.title}</h3>
							<ul className="flex flex-col gap-2.5">
								{column.links.map((link) => (
									<li key={link.label}>
										<a
											href={link.href}
											className="text-[13px] text-white/40 transition-colors hover:text-white/75"
										>
											{link.label}
										</a>
									</li>
								))}
							</ul>
						</div>
					))}
				</div>

				<div className="border-t border-white/[0.08]">
					<div
						className={`${CONTAINER} flex flex-col items-center justify-between gap-2 py-5 text-[12px] text-white/[0.35] sm:flex-row`}
					>
						<span>Enflux © {new Date().getFullYear()}</span>
						<nav className="flex items-center gap-5">
							<Link to="/privacy" className="transition-colors hover:text-white/75">
								Privacy Policy
							</Link>
							<Link to="/terms" className="transition-colors hover:text-white/75">
								Terms of Service
							</Link>
						</nav>
						<span>Site by S-FX.com Small Business Solutions</span>
					</div>
				</div>
			</footer>
		</div>
	);
}
